import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { paramsSignature } from './task-list-url';

/**
 * `placeholderData`: onceki veri YALNIZ ayni listenin (ayni imza) baska sayfasiysa korunur.
 * Sorgu anahtari `[...baseKey, imza, ofset]`; imza `baseKeyLength` konumunda.
 */
export const keepWithinSameList =
  (baseKeyLength: number, signature: string) =>
  <T>(previousData: T | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
    previousQuery?.queryKey[baseKeyLength] === signature ? previousData : undefined;

/**
 * SUNUCU TARAFLI LISTE — sayfalama, arama, filtre, siralama.
 *
 * ============================= SOZLESME ====================================
 * `GET /v1/tasks` ve `/v1/offers` ayni zarfi doner:
 *
 *     { code, message, items, page: { limit, offset, returned, hasMore, sort, snapshotAt } }
 *
 * `total` YOK. Bu yuzden "3 / 17 sayfa" gibi bir gosterge de yok; ileri/geri ve
 * `hasMore` var. Toplam sayiyi uydurmak (orn. returned<limit ise son sayfa)
 * sunucu sayfa sinirinda tam dolu bir sayfa dondugunde yanlis olur.
 *
 * ============================== snapshotAt =================================
 * Ilk sayfanin yanitindaki `snapshotAt` SONRAKI sayfalara geri gonderilir.
 * Gonderilmezse liste her sayfada YENIDEN siralanir: aradaki saniyelerde acilan
 * yeni bir talep her seyi bir sira kaydirir ve kullanici 2. sayfada 1. sayfanin
 * son satirini TEKRAR gorur (ya da bir satiri hic gormez).
 *
 * Deger onbellekten, BIRINCI SAYFANIN yanitindan okunur — ayri bir state'te
 * tutulmuyor. Sebep: ayri state, "filtre degisti, snapshot'i da sifirla" diye
 * ikinci bir sifirlama yolu demek olurdu; bu iki yol bir gun ayrisir.
 *
 * =========================== TEK SIFIRLAMA KAPISI ==========================
 * Parametreler bir IMZAYA cevriliyor; imza degisince ofset sifirlanir. Sekme,
 * filtre, arama ve siralama — hepsi ayni kapidan geciyor. Ayri bir "filtre
 * degisince sifirla" kod yolu YOK; olsaydi biri eklendiginde oteki unutulurdu.
 * ========================================================================== */

/** Sunucu sayfalama ust bilgisi. `total` BILINCLI olarak yok. */
export type ServerPageInfo = {
  limit?: number;
  offset?: number;
  returned?: number;
  hasMore?: boolean;
  sort?: string | null;
  /** Kararli sayfalama damgasi — sonraki sayfalarda geri gonderilir. */
  snapshotAt?: string | null;
};

export type ServerPage<TItem> = {
  items: TItem[];
  page: ServerPageInfo;
  /** Sayfanin alindigi an (serverNow()). Geri sayimlar bunun uzerinden mutlaklasir. */
  receivedAtMs: number;
};

export type ServerListPageArgs = {
  params: Record<string, string>;
  limit: number;
  offset: number;
  snapshotAt: string | null;
};

export type UseServerListArgs<TItem> = {
  /** ['tasks','list'] gibi — anahtar fabrikasindan (task-query-keys.ts). */
  baseKey: readonly string[];
  /** Ekranin sabit parametreleri + filtrelerden gelenler. */
  params: Record<string, string>;
  fetchPage: (args: ServerListPageArgs) => Promise<ServerPage<TItem>>;
  /** Adres cubugundan gelen ofset. */
  offset: number;
  /** Imza degisince cagrilir; ekran ofseti sifirlar (URL'de). */
  onParamsChanged: () => void;
  pageSize?: number;
  /** Oturum henuz hidre olmadiysa istek CIKMAZ. */
  enabled?: boolean;
  /**
   * Canli listeler icin yoklama araligi (ms). Web'de push YOK: yoklanmayan bir liste,
   * baska tarafin yaptigi degisikligi (yeni talep, onay, "odeme alindi") ancak kullanici
   * sayfayi degistirince goruyordu. Buronun piyasasi yeni talebi 2 dakikalik teklif
   * penceresinde hic gormeyebiliyordu. Yalniz sayfa GORUNURKEN yoklanir.
   */
  pollMs?: number | undefined;
};

export const DEFAULT_PAGE_SIZE = 20;

export type UseServerListResult<TItem> = {
  items: TItem[];
  /** Satirlarin alindigi an — geri sayim hesabinin girdisi. */
  receivedAtMs: number;
  isLoading: boolean;
  /** Onceki sayfa ekranda dururken yeni sayfa yukleniyor. */
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  hasMore: boolean;
  hasPrevious: boolean;
  offset: number;
  pageSize: number;
  refetch: () => void;
};

export function useServerList<TItem>({
  baseKey,
  params,
  fetchPage,
  offset,
  onParamsChanged,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true,
  pollMs,
}: UseServerListArgs<TItem>): UseServerListResult<TItem> {
  const client = useQueryClient();
  const signature = paramsSignature(params);

  // Imza degisimi TEK sifirlama kapisi. Efekt icinde state yazilmiyor — ekran
  // kendi URL'sini duzeltiyor, boylece sifirlama da adres cubuguna yansiyor.
  const previousSignature = useRef(signature);
  useEffect(() => {
    if (previousSignature.current === signature) return;
    previousSignature.current = signature;
    if (offset !== 0) onParamsChanged();
  }, [signature, offset, onParamsChanged]);

  // Ilk sayfanin yaniti onbellekte duruyorsa damgasi oradan okunur. Yoksa null
  // gider ve sunucu yeni bir damga uretir — tutarlilik bir sayfa icin bozulur,
  // liste bos gelmez. (Damgayi ayri bir state'te tasimak ikinci bir sifirlama
  // yolu acardi; bkz. dosya basi.)
  const firstPage = client.getQueryData<ServerPage<TItem>>([...baseKey, signature, 0]);
  const snapshotAt = offset === 0 ? null : (firstPage?.page.snapshotAt ?? null);

  const query = useQuery({
    queryKey: [...baseKey, signature, offset],
    queryFn: () => fetchPage({ params, limit: pageSize, offset, snapshotAt }),
    enabled,
    // Sayfa degisiminde liste bos bir iskelete DUSMEZ; onceki sayfa yerinde
    // kalir ve ustunde "yukleniyor" isareti gorunur. YALNIZ ayni imzada (ayni sekme
    // ve filtre, farkli ofset): sekme degisince onceki sekmenin satirlari yeni sekmenin
    // altinda gosteriliyordu — onaydan sonra Onaylanan'a gecen musteri Acik Talepler'i
    // goruyor, "onaylanan gorunmuyor" saniyordu (M5, 2026-10-02). Imza degisince iskelet.
    placeholderData: keepWithinSameList(baseKey.length, signature),
    // Sekmeye donuste tazele (global varsayilan false, lib/queryClient.ts). Geri sayim son
    // tarihten hesaplandigi icin sure dogru gorunuyor, ama liste UYELIGI (suresi dolan talep,
    // onaylanan teklif) ancak yeniden cekince duzelir.
    refetchOnWindowFocus: true,
    refetchInterval: pollMs ?? false,
    refetchIntervalInBackground: false,
  });

  const data = query.data;
  const items = useMemo(() => data?.items ?? [], [data]);

  return {
    items,
    receivedAtMs: data?.receivedAtMs ?? 0,
    isLoading: query.isPending && enabled,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    hasMore: data?.page.hasMore === true,
    hasPrevious: offset > 0,
    offset,
    pageSize,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * GECIKMELI DEGER — arama kutusu icin.
 *
 * Her tus vurusu adres cubugunu ve dolayisiyla bir istegi tetiklemesin diye
 * ~250 ms bekler. Sure bilincli olarak kisa: daha uzunu, kullanicinin yazmayi
 * bitirdikten sonra "takildi mi" diye dusundugu bir bosluk uretiyor.
 */
export const useDebouncedValue = <T,>(value: T, delayMs = 250): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (Object.is(debounced, value)) return undefined;
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs, debounced]);

  return debounced;
};
